import { Injectable, inject, signal, computed, effect } from '@angular/core';
import { CartItem, AddToCartPayload, ProductDetail } from '../core/models/interfaces-general';
import { CarritoBackService, CarritoResponse } from './carrito-back.service';
import { CapacidadService } from '../core/services/capacidad.service';
import { RangoHorarioApi } from '../core/models/api/empresa-api';
import { AuthService } from '../core/services/auth.service';
import { Constante } from '../constante';

@Injectable({
    providedIn: 'root'
})
export class CartService {
    private readonly api = inject(CarritoBackService);
    private readonly capacidad = inject(CapacidadService);
    private readonly auth = inject(AuthService);

    cartUuid = signal<string | null>(localStorage.getItem(Constante.CART_UUID));
    expiraAt = signal<string | null>(localStorage.getItem(Constante.CART_EXPIRA));

    cartItems = signal<CartItem[]>([]);
    serverSubtotal = signal<number | null>(null);
    timeRemaining = signal<string>('--:--');
    isLoading = signal<boolean>(false);
    errorMsg = signal<string | null>(null);

    private intervalId: ReturnType<typeof setInterval> | null = null;
    private rangosCache: RangoHorarioApi[] | null = null;
    private claimedForSession = false;
    private createFlight: Promise<string | null> | null = null;

    subtotal = computed(() => {
        const server = this.serverSubtotal();
        if (server !== null) {
            return server;
        }
        return this.cartItems().reduce((acc, item) => acc + item.price * item.quantity, 0);
    });

    itemCount = computed(() => {
        return this.cartItems().reduce((acc, item) => acc + item.quantity, 0);
    });

    constructor() {
        effect(() => {
            const session = this.auth.session();
            const uuid = this.cartUuid();
            if (session && uuid && !this.claimedForSession) {
                this.claimedForSession = true;
                this.claimPendingCart();
            }
            if (!session) {
                this.claimedForSession = false;
            }
        });

        if (this.expiraAt()) {
            this.startCountdown();
        }

        if (this.cartUuid()) {
            this.loadCartFromBackend(this.cartUuid()!);
        }
    }

    addToCart(product: ProductDetail, payload: AddToCartPayload, after?: () => void) {
        if (this.isLoading()) {
            return;
        }
        this.errorMsg.set(null);
        this.isLoading.set(true);

        this.ensureUuid().then((uuid) => {
            if (!uuid) {
                this.isLoading.set(false);
                return;
            }
            this.resolveRangoUuid(payload.deliveryTimeSlot).then((rangoUuid) => {
                if (!rangoUuid) {
                    this.isLoading.set(false);
                    this.errorMsg.set('El horario de entrega ya no está disponible.');
                    return;
                }
                const body = {
                    productoUuid: payload.productId.toString(),
                    cantidad: payload.quantity,
                    fechaEntrega: payload.deliveryDate,
                    rangoHorarioUuid: rangoUuid,
                    complementoUuids: (payload.addons ?? []).map((a) => a.id.toString())
                };
                this.sendAdd(uuid, body, false, after);
            });
        });
    }

    private sendAdd(uuid: string, body: {
        productoUuid: string;
        cantidad: number;
        fechaEntrega: string;
        rangoHorarioUuid: string;
        complementoUuids: string[];
    }, retried: boolean, after?: () => void) {
        this.api.addItem(uuid, body).subscribe({
            next: (res) => {
                if (res.success === Constante.REQUEST_STATUS_OK) {
                    this.loadCartFromBackend(uuid, after);
                    return;
                }
                if (this.isCartGone(res) && !retried) {
                    const current = this.cartUuid();
                    if (current && current !== uuid) {
                        this.sendAdd(current, body, true, after);
                        return;
                    }
                    this.clearCart();
                    this.ensureUuid().then((fresh) => {
                        if (!fresh) {
                            this.isLoading.set(false);
                            return;
                        }
                        this.sendAdd(fresh, body, true, after);
                    });
                    return;
                }
                this.isLoading.set(false);
                this.errorMsg.set(this.readError(res));
            },
            error: () => {
                this.isLoading.set(false);
                this.errorMsg.set('No se pudo agregar el producto.');
            }
        });
    }

    updateQuantity(itemId: string, quantity: number) {
        const uuid = this.cartUuid();
        const target = this.cartItems().find((i) => i.id === itemId);
        if (!uuid || !target || quantity < 1 || this.isLoading()) {
            return;
        }
        this.errorMsg.set(null);
        this.isLoading.set(true);
        this.resolveRangoUuid(target.deliveryTimeSlot).then((rangoUuid) => {
            const body = {
                productoUuid: target.productId,
                cantidad: quantity,
                fechaEntrega: target.deliveryDate,
                rangoHorarioUuid: rangoUuid ?? '',
                complementoUuids: [] as string[]
            };
            this.api.updateItem(uuid, itemId, body).subscribe({
                next: (res) => {
                    if (res.success === Constante.REQUEST_STATUS_OK) {
                        this.loadCartFromBackend(uuid);
                    } else {
                        this.isLoading.set(false);
                        this.errorMsg.set(this.readError(res));
                    }
                },
                error: () => {
                    this.isLoading.set(false);
                    this.errorMsg.set('No se pudo actualizar la cantidad.');
                }
            });
        });
    }

    removeItem(itemId: string) {
        const uuid = this.cartUuid();
        if (!uuid || this.isLoading()) {
            return;
        }
        this.errorMsg.set(null);
        this.isLoading.set(true);
        this.api.removeItem(uuid, itemId).subscribe({
            next: (res) => {
                if (res.success === Constante.REQUEST_STATUS_OK) {
                    this.loadCartFromBackend(uuid);
                } else {
                    this.isLoading.set(false);
                    this.errorMsg.set(this.readError(res));
                }
            },
            error: () => {
                this.isLoading.set(false);
                this.errorMsg.set('No se pudo eliminar el producto.');
            }
        });
    }

    clearCart() {
        this.cartItems.set([]);
        this.serverSubtotal.set(null);
        this.setIdentity(null, null);
        this.errorMsg.set(null);
        if (this.intervalId) {
            clearInterval(this.intervalId);
            this.intervalId = null;
        }
        this.timeRemaining.set('--:--');
    }

    private claimPendingCart() {
        const uuid = this.cartUuid();
        if (!uuid) {
            return;
        }
        this.api.claimCarrito(uuid).subscribe({
            next: (res) => {
                console.debug('[cart] claim', uuid, 'success=' + res.success, JSON.stringify(res.errors ?? null));
                if (res.success === Constante.REQUEST_STATUS_OK && res.data) {
                    this.syncFromResponse(res.data);
                }
            },
            error: () => undefined
        });
    }

    private ensureUuid(): Promise<string | null> {
        const current = this.cartUuid();
        if (current) {
            return Promise.resolve(current);
        }
        if (!this.createFlight) {
            this.createFlight = new Promise<string | null>((resolve) => {
                this.api.createCarrito().subscribe({
                    next: (res) => {
                        console.debug('[cart] create', 'success=' + res.success, 'uuid=' + res.data?.uuid, JSON.stringify(res.errors ?? null));
                        if (res.success === Constante.REQUEST_STATUS_OK && res.data) {
                            this.syncFromResponse(res.data);
                            resolve(res.data.uuid);
                        } else {
                            this.errorMsg.set(this.readError(res));
                            resolve(null);
                        }
                    },
                    error: () => {
                        this.errorMsg.set('No se pudo crear el carrito.');
                        resolve(null);
                    }
                });
            });
            this.createFlight.then(() => {
                this.createFlight = null;
            });
        }
        return this.createFlight;
    }

    private setIdentity(uuid: string | null, expira: string | null) {
        this.cartUuid.set(uuid);
        this.expiraAt.set(expira);
        this.store(Constante.CART_UUID, uuid);
        this.store(Constante.CART_EXPIRA, expira);
    }

    private store(key: string, value: string | null) {
        if (value) {
            localStorage.setItem(key, value);
        } else {
            localStorage.removeItem(key);
        }
    }

    private syncFromResponse(data: CarritoResponse) {
        this.setIdentity(data.uuid, data.expiraAt);
        this.mapItems(data);
        this.startCountdown();
    }

    private loadCartFromBackend(uuid: string, after?: () => void) {
        this.isLoading.set(true);
        this.api.getCarrito(uuid).subscribe({
            next: (res) => {
                if (res.success === Constante.REQUEST_STATUS_OK && res.data) {
                    this.syncFromResponse(res.data);
                    if (after) {
                        after();
                    }
                } else if (this.isCartGone(res)) {
                    this.clearCart();
                } else {
                    this.errorMsg.set(this.readError(res));
                }
                this.isLoading.set(false);
            },
            error: () => {
                this.isLoading.set(false);
            }
        });
    }

    private mapItems(data: CarritoResponse) {
        const slotByUuid = new Map((this.rangosCache ?? []).map((r) => [r.uuid, r.nombre]));
        const mapped: CartItem[] = data.items.map((item) => ({
            id: item.uuid,
            productId: item.productoUuid,
            title: item.nombre,
            image: item.urlImagen ?? '',
            price: item.precioUnitario,
            quantity: item.cantidad,
            deliveryDate: item.fechaEntrega ?? '',
            deliveryTimeSlot: item.rangoHorarioUuid ? (slotByUuid.get(item.rangoHorarioUuid) ?? '') : '',
            addons: (item.complementos ?? []).map((name) => ({ id: '', name, price: 0, imageUrl: '' }))
        }));
        this.cartItems.set(mapped);
        this.serverSubtotal.set(data.subtotal);

        if (mapped.length === 0) {
            this.clearCart();
        }
    }

    private resolveRangoUuid(slot: string): Promise<string | null> {
        const load = this.rangosCache
            ? Promise.resolve(this.rangosCache)
            : new Promise<RangoHorarioApi[]>((resolve) => {
                this.capacidad.listRangosHorario().subscribe({
                    next: (rangos) => {
                        this.rangosCache = rangos;
                        resolve(rangos);
                    },
                    error: () => resolve([])
                });
            });
        return load.then((rangos) => {
            const norm = (s: string) => s.toLowerCase().replace(/[\s.\-_:]/g, '');
            const want = norm(slot);
            const found = rangos.find((r) => norm(r.nombre) === want || norm(r.uuid) === want);
            return found ? found.uuid : null;
        });
    }

    private isCartGone(res: { success: number; errors: unknown }): boolean {
        const errors = res.errors as { code?: string }[] | null;
        return res.success === Constante.REQUEST_STATUS_ERROR && !!errors?.some((e) => e?.code === '1002');
    }

    private readError(res: { success: number; message: string; errors: unknown }): string {
        const errors = res.errors as { message?: string }[] | null;
        return errors?.[0]?.message || res.message || 'Ocurrió un error inesperado.';
    }

    private startCountdown() {
        if (this.intervalId) {
            clearInterval(this.intervalId);
        }
        const target = this.expiraAt() ? Date.parse(this.expiraAt()!) : NaN;
        if (Number.isNaN(target)) {
            return;
        }
        this.intervalId = setInterval(() => {
            const diff = target - Date.now();
            if (diff <= 0) {
                this.clearCart();
                alert('El tiempo de reserva ha expirado y tus productos se han liberado.');
            } else {
                const minutes = Math.floor(diff / 60000);
                const seconds = Math.floor((diff % 60000) / 1000);
                this.timeRemaining.set(
                    `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`
                );
            }
        }, 1000);
    }
}
